import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { Form, theme, FormInstance, Input, InputNumber, Avatar, Row, Col, Tooltip } from 'antd';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { ApiOutlined } from '@ant-design/icons';
import { SiteConfigContainer } from './SiteConfigurationSection.styles';
import { NUMBER_OPTIONS, PressureType, STATION_NAME_SUFFIXES } from '@/store/SiteSurveyForm/constants';
import MonitorIcon from '@/comp/common/icons/MonitorIcon';
import WarehouseIcon from '@/comp/common/icons/WarehouseIcon';
import LightningIcon from '@/comp/common/icons/LightningIcon';
import { Room } from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/RoomConfigurationContent.utils';
import { useStores } from '@/hooks/use-stores';
import {
  handleCompressorRoomsChange,
  handleEntryPointsChange,
  handleOtherCompressorRoomsChange,
  handleOtherEntryPointsChange,
  handlePressureChanged,
  handleTargetPressureChange,
  generateStationNameParts,
  getDefaultPressureName,
} from '@/comp/Forms/SiteSurveyForm/SiteConfigurationStep/SiteConfigurationContent.utils';

const { useToken } = theme;

interface SiteConfigSectionProps {
  form: FormInstance;
  setRooms?: Dispatch<SetStateAction<Room[]>>;
}

const SiteConfigurationSection: React.FC<SiteConfigSectionProps> = observer(({ form, setRooms }) => {
  const { token } = useToken();
  const { siteSurveyStore } = useStores();
  const compressorRooms = Form.useWatch('compressorRooms', form) || 1;
  const otherCompressorRooms = Form.useWatch('otherCompressorRooms', form);
  const entryPoints = Form.useWatch('entryPoints', form) || 1;
  const otherEntryPoints = Form.useWatch('otherEntryPoints', form);
  const hasMultipleTargetPressures = Form.useWatch('hasMultipleTargetPressures', form) || false;
  const pressureType = Form.useWatch('pressureType', form);
  const account = Form.useWatch('account', form);
  const customerName = Form.useWatch('newCustomerName', form);
  const tier = Form.useWatch('tier', form);
  const stationNameSuffix = Form.useWatch('stationNameSuffix', form);
  const siteAddress = Form.useWatch('siteAddress', form);

  const { fullName: computedStationName, basePart: stationNameBasePart } = useMemo(
    () => generateStationNameParts(siteSurveyStore, form),
    [account, customerName, stationNameSuffix, siteAddress, siteSurveyStore, form],
  );

  useEffect(() => {
    form.setFieldValue('stationName', computedStationName);

    const currentSuffix = form.getFieldValue('stationNameSuffix');
    const shouldUseDefaultSuffix =
      currentSuffix === undefined ||
      currentSuffix === STATION_NAME_SUFFIXES.MAIN ||
      currentSuffix === STATION_NAME_SUFFIXES.INTERNAL;

    if (shouldUseDefaultSuffix) {
      const defaultSuffix = getDefaultPressureName(form);
      form.setFieldValue('stationNameSuffix', defaultSuffix);
    }
  }, [computedStationName, tier, hasMultipleTargetPressures, pressureType]);

  return (
    <SiteConfigContainer token={token}>
      <div className="site-config-title">{t('Define site configuration')}</div>
      <div className="site-config-content">
        <Row className="config-row" align="middle" gutter={[24, 16]}>
          <Col xs={24} md={{ flex: 'auto' }}>
            <div className="label-section">
              <Avatar className="avatar" icon={<WarehouseIcon />} />
              <div className="label-text">{t('Number of compressor rooms')}</div>
            </div>
          </Col>
          <Col xs={24} md={{ flex: '294px' }}>
            <div className="control-section">
              <Form.Item name="compressorRooms" initialValue={1} noStyle>
                <div className="radio-container">
                  <div className="compact-radio-group">
                    {NUMBER_OPTIONS.map(num => (
                      <button
                        className={`compact-radio-button ${compressorRooms === num ? 'active' : ''}`}
                        key={num}
                        onClick={() => handleCompressorRoomsChange(num, form)}
                        type="button"
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                  <div className={`other-input ${compressorRooms === 'other' ? 'active' : ''}`}>
                    <Form.Item name="otherCompressorRooms" noStyle>
                      <InputNumber
                        className={`input-number-other ${compressorRooms === 'other' ? 'active' : ''}`}
                        value={otherCompressorRooms}
                        min={1}
                        max={15}
                        size="large"
                        onBlur={e => {
                          const value = e.target.value ? parseInt(e.target.value, 10) : null;
                          handleOtherCompressorRoomsChange(value, form);
                        }}
                        placeholder={t('Other')}
                      />
                    </Form.Item>
                  </div>
                </div>
              </Form.Item>
            </div>
          </Col>
        </Row>

        <Row className="config-row" align="middle" gutter={[24, 16]}>
          <Col xs={24} md={{ flex: 'auto' }}>
            <div className="label-section">
              <Avatar className="avatar" icon={<ApiOutlined />} />
              <div className="label-text">{t('Number of production floors')}</div>
            </div>
          </Col>
          <Col xs={24} md={{ flex: '294px' }}>
            <div className="control-section">
              <Form.Item name="entryPoints" initialValue={1} noStyle>
                <div className="radio-container">
                  <div className="compact-radio-group">
                    {NUMBER_OPTIONS.map(num => (
                      <button
                        className={`compact-radio-button ${entryPoints === num ? 'active' : ''}`}
                        key={num}
                        onClick={() => handleEntryPointsChange(num, entryPoints, form)}
                        type="button"
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                  <div className={`other-input ${entryPoints === 'other' ? 'active' : ''}`}>
                    <Form.Item name="otherEntryPoints" noStyle>
                      <InputNumber
                        className={`input-number-other ${entryPoints === 'other' ? 'active' : ''}`}
                        type="number"
                        value={otherEntryPoints}
                        min={1}
                        max={15}
                        size="large"
                        onBlur={e => handleOtherEntryPointsChange(e, form)}
                        placeholder={t('Other')}
                      />
                    </Form.Item>
                  </div>
                </div>
              </Form.Item>
            </div>
          </Col>
        </Row>

        <Row className="config-row" align="middle" gutter={[24, 16]}>
          <Col xs={24} md={{ flex: 'auto' }}>
            <div className="label-section">
              <Avatar className="avatar" icon={<MonitorIcon />} />
              <div className="label-text">{t('System controller type (If installed)')}</div>
            </div>
          </Col>
          <Col xs={24} md={{ flex: '294px' }}>
            <div className="control-section">
              <Form.Item name="systemControllerType" noStyle>
                <Input placeholder={t('Add system controller type')} className="system-controller-input" />
              </Form.Item>
            </div>
          </Col>
        </Row>

        <Row className="config-row" align="middle" gutter={[24, 16]}>
          <Col xs={24} md={{ flex: 'auto' }}>
            <div className="label-section">
              <Avatar className="avatar" icon={<LightningIcon />} />
              <div className="label-text">{t('Is there more than 1 target pressure?')}</div>
            </div>
          </Col>
          <Col xs={24} md={{ flex: '294px' }}>
            <div className="control-section">
              <Form.Item name="hasMultipleTargetPressures" noStyle>
                <>
                  <div className="fixed-width-radio-group">
                    <button
                      className={`pressure-radio-button ${!hasMultipleTargetPressures ? 'active' : ''}`}
                      onClick={() => handleTargetPressureChange(false, hasMultipleTargetPressures, form, setRooms)}
                      type="button"
                    >
                      {t('No')}
                    </button>
                    <button
                      className={`pressure-radio-button ${hasMultipleTargetPressures ? 'active' : ''}`}
                      onClick={() => handleTargetPressureChange(true, hasMultipleTargetPressures, form, setRooms)}
                      type="button"
                    >
                      {t('Yes')}
                    </button>
                  </div>
                </>
              </Form.Item>
            </div>
          </Col>
        </Row>
        {hasMultipleTargetPressures && (
          <>
            <Row className="indented-config-row" align="middle" gutter={[24, 16]}>
              <Col xs={24} md={{ flex: 'auto' }}>
                <div className="label-section">
                  <div>
                    <div className="label-text">{t('What is the pressure at this station?')}</div>
                    <div className="pressure-note-text">
                      {t('Note: Each pressure type requires a separate site survey')}
                    </div>
                  </div>
                </div>
              </Col>
              <Col xs={24} md={{ flex: '294px' }}>
                <div className="control-section">
                  <Form.Item name="pressureType" noStyle>
                    <div className="fixed-width-radio-group">
                      {Object.values(PressureType).map(option => (
                        <button
                          key={option}
                          className={`pressure-radio-button ${pressureType === option ? 'active' : ''}`}
                          onClick={() => handlePressureChanged(option, form, setRooms)}
                          type="button"
                        >
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </button>
                      ))}
                    </div>
                  </Form.Item>
                </div>
              </Col>
            </Row>
          </>
        )}

        <Row className="config-row" align="middle" gutter={[24, 16]}>
          <Col xs={24} md={{ flex: 'auto' }}>
            <div className="label-section">
              <div className="label-text">{t("Station's name")}</div>
            </div>
          </Col>
          <Col xs={24} md={{ flex: '294px' }}>
            <div className="control-section">
              <div className="station-name-container">
                <Tooltip title={computedStationName} placement="topLeft">
                  <div className="station-name-display">
                    <span className="station-name-text">{stationNameBasePart && `${stationNameBasePart} | `}</span>
                  </div>
                </Tooltip>
                <Form.Item name="stationNameSuffix" noStyle>
                  <Input placeholder={t('Type')} className="system-controller-input station-name-suffix-input" />
                </Form.Item>
              </div>
              <Form.Item name="stationName" noStyle>
                <Input type="hidden" />
              </Form.Item>
            </div>
          </Col>
        </Row>
      </div>
    </SiteConfigContainer>
  );
});

export default SiteConfigurationSection;
